import { Component, createRef, RefObject } from 'react';

import classNames from 'classnames';
import ClustersLayer from './ClustersLayer';
import Loader from './Loader';
import styles from './Map.module.scss';
import MuseumsLayer from './MuseumsLayer';
import { debounce, MercatorProjection } from '@/components/features/map/Map.utils';
import { NextRouter, withRouter } from 'next/router';

const MAX_MAP_OFFSET = 0.35;
const RESIZE_DEBOUNCE_TIMEOUT = 30;

let xCenter = 0;

interface WithRouterProps {
	router: NextRouter;
}

interface IState {
	styles: { transform: string };
	currentOffset: { x: number; y: number };
	leftXOffset: number;
	moveable: boolean;
	initialized: boolean;
	startAnimation: boolean;
	hoveredElement: any;
}

export interface IMapProps extends WithRouterProps {
	museums?: Array<any>;
	clusters?: Array<any>;
	mapWidth?: number;
	mapHeight?: number;
	verticalMapOffset?: number;
	loaderDelay?: number;
	clusterAnimationDelay?: number;
	mapPathClassName?: string;
	hideClusters?: boolean;
	className?: string;
	mapWrapperClassName?: string;
}

class Map extends Component<IMapProps> {
	static defaultProps = {
		mapWidth: 1200,
		mapHeight: 1193,
		verticalMapOffset: -150,
		loaderDelay: 0,
		clusterAnimationDelay: 500,
	};

	private _startPosition: { x: number; y: number };
	private _isMounted: boolean;
	private _timeout: string | number | NodeJS.Timeout;
	private _moveCheckTimeout: string | number | NodeJS.Timeout;
	private _startAnimationDelay: string | number | NodeJS.Timeout;
	private _oldOffset: any;
	private _dragging: boolean;
	projection: MercatorProjection;
	switchPoint: { posX: number; posY: number };
	state: IState;
	container: RefObject<HTMLDivElement>;
	map: RefObject<SVGSVGElement>;

	constructor(props: IMapProps) {
		super(props);

		this.state = {
			styles: this.getTranslateStyles(0),
			currentOffset: {
				x: 0,
				y: this.props.verticalMapOffset,
			},
			leftXOffset: xCenter,
			moveable: true,
			initialized: false,
			startAnimation: false,
			hoveredElement: undefined,
		};

		this._startPosition = {
			x: 0,
			y: this.props.verticalMapOffset,
		};

		this.projection = new MercatorProjection({
			mapWidth: this.props.mapWidth,
			mapHeight: this.props.mapHeight,
		});

		this.switchPoint = this.projection.geoToPixel(0, 0);

		this.container = createRef<HTMLDivElement>();
		this.map = createRef<SVGSVGElement>();
	}

	componentDidMount() {
		this._isMounted = true;
		this.checkCanMove();
		this.computeMapPosition();
		window.addEventListener('resize', debounce(this.onResize.bind(this), RESIZE_DEBOUNCE_TIMEOUT));
	}

	componentWillUnmount() {
		this._isMounted = false;
		clearTimeout(this._timeout);
		clearTimeout(this._moveCheckTimeout);
		clearTimeout(this._startAnimationDelay);
	}

	onResize() {
		this.checkCanMove();
		this.computeMapPosition();
	}

	onMouseDown(event) {
		const { screenX, screenY } = this.getPositionFromEvent(event);

		this._oldOffset = { ...this.state.currentOffset };

		this._startPosition = {
			x: screenX - this.state.currentOffset.x,
			y: screenY - this.state.currentOffset.y,
		};

		this._dragging = true;
	}

	onMouseUp() {
		this._dragging = false;
	}

	onMouseMove(event) {
		if (!this.state.moveable) {
			return;
		}

		const { screenX, screenY } = this.getPositionFromEvent(event);

		if (this._dragging) {
			const offset = this.normalizePosition({
				x: screenX - this._startPosition.x,
				y: screenY - this._startPosition.y,
			});

			const move = {
				x: Math.abs(offset.x - this._oldOffset.x),
				y: Math.abs(offset.y - this._oldOffset.y),
			};

			if (move.x >= move.y) {
				event.preventDefault();
				this.setOffset(offset);
			}
		}
	}

	getTranslateStyles(offsetX) {
		return {
			transform: `translate(${offsetX}px, ${this.props.verticalMapOffset}px)`,
			WebkitTransform: `translate(${offsetX}px, ${this.props.verticalMapOffset}px)`,
		};
	}

	getPositionFromEvent(event) {
		const screenX = event.touches && event.touches[0] ? event.touches[0].screenX : event.screenX;

		const screenY = event.touches && event.touches[0] ? event.touches[0].screenY : event.screenY;

		return {
			screenX,
			screenY,
		};
	}

	setOffset({ x }) {
		this.setState({
			styles: this.getTranslateStyles(x),
			currentOffset: {
				x,
				y: this.props.verticalMapOffset,
			},
		});
	}

	checkCanMove() {
		this._moveCheckTimeout = setTimeout(() => {
			if (this._isMounted) {
				this.setState({
					moveable: document.body.offsetWidth < this.props.mapWidth,
				});
			}
		}, 0);
	}

	computeMapPosition() {
		const parent = this.container.current;
		const map = this.map.current;

		if (map && parent) {
			const { width: parentWidth } = parent.getBoundingClientRect();

			const { width: mapWidth } = map.getBoundingClientRect();

			const distance = (parentWidth - mapWidth) / 2;
			xCenter = distance;

			this._timeout = setTimeout(() => {
				this.setState({
					leftXOffset: distance,
					initialized: true,
				});

				this._startAnimationDelay = setTimeout(() => {
					this.setState({
						startAnimation: true,
					});
				}, this.props.clusterAnimationDelay);
			}, this.props.loaderDelay);
		}
	}

	normalizePosition({ x, y }) {
		const signumX = x > 0 ? 1 : -1;
		const signumY = y > 0 ? 1 : -1;

		const absX = Math.abs(x);
		const absY = Math.abs(y);

		const { width, height } = this.map.current.getBoundingClientRect();

		return {
			x: signumX * Math.min(width * MAX_MAP_OFFSET, absX),
			y: signumY * Math.min(height * MAX_MAP_OFFSET, absY),
		};
	}

	openCluster({ latitude, longitude }) {
		if (!this._dragging) {
			const coords = {
				lat: latitude,
				lng: longitude,
			};
			this.props.router.replace({ query: { open: 'breakups-map', ...coords } });
		}
	}

	isCategoryHidden(category, hiddenCategories) {
		return hiddenCategories && hiddenCategories.includes(category);
	}

	focusElement(elementId) {
		// this.props.addHoveredElement(elementId);
		this.setState({
			hoveredElement: elementId,
		});
	}

	unfocusElement() {
		// this.props.clearHoveredElement();
		this.setState({
			hoveredElement: undefined,
		});
	}

	isLoaded() {
		return this.state.initialized && Boolean(this.props.museums) && Boolean(this.props.clusters);
	}

	render() {
		const { clusters, museums, mapPathClassName, mapWidth, mapHeight, hideClusters } = this.props;

		const loaded = this.isLoaded();

		const mapClassNames = classNames(styles.map, this.props.className);

		const mapWrapperClassNames = classNames(styles.mapWrapper, this.props.mapWrapperClassName);

		const mapPathClassNames = classNames({
			[styles.mapPath]: !mapPathClassName,
			[this.props.mapPathClassName]: mapPathClassName,
		});

		const wrapperStyles = {
			...this.state.styles,
			left: `${this.state.leftXOffset}px`,
			opacity: loaded ? 1 : 0,
		};

		return (
			<div ref={this.container} className={mapClassNames}>
				<Loader done={loaded} />
				<div
					onMouseDown={this.onMouseDown.bind(this)}
					onMouseMove={this.onMouseMove.bind(this)}
					onTouchStart={this.onMouseDown.bind(this)}
					onTouchMove={this.onMouseMove.bind(this)}
					onMouseUp={this.onMouseUp.bind(this)}
					onTouchEnd={this.onMouseUp.bind(this)}
					style={wrapperStyles}
					className={mapWrapperClassNames}
				>
					<svg
						ref={this.map}
						width={mapWidth}
						height={mapHeight}
						viewBox="0 0 1200 1193"
						xmlns="http://www.w3.org/2000/svg"
						className={styles.mapSvg}
					>
						<defs>
							<filter id="blur" x="-50%" y="-50%" width="200%" height="200%">
								<feGaussianBlur in="SourceGraphic" stdDeviation="15" />
								<feComponentTransfer>
									<feFuncA type="linear" slope="0.08" />
								</feComponentTransfer>
							</filter>
						</defs>
						<path
							filter="url(#blur)"
							fill="#000000"
							transform="translate(44, 155)"
							d="M949.28 368.16l1.2.9-3.35 8.82-7.24-1.2-1.12-3.8 6.6-3.58-29.4 1.58-7.05 11.52 15.02 22.15L908 417.83l-16.3-16.9-.54 15.98 14.2 16 6.16 28.36 17.77 6.22 23.88 9.72-44.26-7.95-18.6-17.77-16.85-23.88 23.03 11.52-10.65-19.47-2.12-26.5-10.27 2.6-12.4-26.6-34.5 23.95v15.07l-12.44 12.36-15.95-39.83-17.65-21.28-42.57-7.98-12.37-10.6-6.22 3.5 8.87 21.3 14.15-7.08 18.66 14.15-54.92 32.77-28.38-55.8-5.9 1.02 28.94 66.3 28.36-13.3-8.86 31.02-28.34 25.7 4.4 38.04-15.94 12.4-29.2 54.92-29.24 5.32-25.64-62 6.14-28.32-15.9-49.6-54.02 3.55-30.17-29.25 3.72-33.25 162.23-3.12s-161.23-5.92-161.1-7.1l.48-4.33 29.26-35.43 56.66-9.7 3.56 15 23 15.06 8.86-12.4 35.2 9.02 9.13-5.47 3.95-13.28-24.3-4.05-7.86-13.63-5.74-2.9-5.03 17.25-10.74-13.68.7-11.4-14.36-11.5-8.6-4.3.73 11.5 17.95 12.13-9.38 10.82-12.88-17.25-9.27-10.77L567 293.47l-15.04 23.7-22.95-3.6-1.4-28.7 29.33 2.15-9.25-15.04-5.76-12.2 22.22-7.17 12.18-16.47 10.06-.73-2.16-9.33 6.44-8.6 5.02 14.34 28 .73 7.84-20.1 10.82 3.64-3.63-13.62 16.68-5.4-5.18-6.1-15.08 4.27-5.65-13.86-2.2-5.45 6.05-4.33 5.3-13.45-5.68-.9-9.25 11.5-7.92 17.2 8.63 9.3-8.62 22.96-12.86 5-7.23-22.9-15.05 8.57-7.18-4.27.7-22.22 12.95-10.07 10.73-26.53 55.3-28.05 40 25.54C771.65 135 902 101.93 902 101.93l246.94 51.16.86 24.76-23.9 17.74-23.88 5.26.9 20.4-23.05 28.34-.87-31.86 30.08-36.34-18.57 2.7-23.92 19.44-29.25-1.76-6.1 5.88H872.07l150.1 10.08-6.96 5.32 15.92 11.5-3.67 10.15h4.74l2.63 10.7-5.24 18.55 9.66 7.97-12.37 11.5-1.77 23.03-37.2 22.13.9-13.24 20.38-15.1 15.93-33.65.8-27.6-13.36 36.96-15.95 2.64-9.73 14.16 4.4 18.63-11.5 6.13-3.53-16.8-9.78-13.3-13.3 14.2 10.6 30.06-14.52 26.46zm-550.92 55.8l-38.63-23.77-54.33-.7-6.86 13.77-11.23-2.66-7.1-19.7-16.22.28 5.03-19.7-13.55-.17-12.53 12.02-10.6-12.7 6.07-23.57 35.9-8.58 8.3 18.25 9.37-2.14-1.53-17.53 22.5-37.23 30.76-20.34 40.06-22.44-35.4-63.43-12.73 9.26-13.3-28.66-25.53 3.58-2.03 53.1-9.34 2.2-42.73-38.35 6.2-22.26 39.74-11.6-4.48-51.18-100.08 19.62-55.14-26.38-22.25 25-69.2-30.6-34.42 24.73 15.22 19.06-14.83 1.3-2.73 15.13 20.42.93L2.4 176.4l8.7 22.3 17.86 2.52-26.24 25.36 42.53-25.42 23.03-15.63 41.17 20.86 5.15 27.03L221.68 231l-99.33 13.27 22.67 11.9 4.12 47.13 21.9 33.37 35.2 45.75 38.88 10.12 16.85 6.7 20.94 22.57 12.98-3.17-12.4 24.87 16.32 47.36 27.6 20.63-20.06 127.98 13.2 25.97 31.64 5.37-20.55-18.58 21.27-50.68 51.07-51.95 2.15-21.88 26.6-6.42 10-26.1 13.28-5.48-10.24-2.4 9.05-23.56-49.1-18.73-7.34-21.08zm627.06 52.85l-2.9 22.97-19.45-7.07 3.6-12.43-21.28-1.72-27.58 25.7-29.25 10.58 7.16 39.8-2.7 7.9 10.7 6.2 43.36-10.72 16.76 9.77 7.98-4.45 5.32 16.1 22.1 6.2 15.98-9.77 8.85-47.72-23.9-23.9-14.78-27.42zm-107.45-22.15l23.02 4.4 10.65-32.74-6.24-5.35-30.96 19.5 3.53 14.2zm133.7 23.9l-7.94-16.84-36.3-17.67-18.6 1.74 23.9 24.8 13.7.97 5.78-5.4 19.46 12.4zm76.2 87.66l2.66 24.82-27.45 25.7 17.73 1.77 15.9-23.95 12.4-16.8-7.95-.85-13.3-10.7zm-587.9-340.68v25.8l17.9-7.86 5.03-7.93-19.38-37.27-11.4 7.9.7 10.02-10.78 12.97-.75 13.58h12.94l5.73-17.2zm157.97 271.86l-.72 28.7 11.53 5 11.44-34.4-2.9-14.33-19.36 15.03zM418.5 6.88l-59.52-5.64-63.75 53.05 95.23 120 32.62 5.97 2.65-43.4 22.03-10.14 1.27-16.5 22.73-31.8-15.83-24.14L418.5 6.87zm64.9 156.18l7.97-16.3 17.68 8.62-6.25 12.82-11.46 3.95-7.93-9.1z"
						/>

						<path
							className={mapPathClassNames}
							transform="translate(44, 155)"
							d="M949.28 368.16l1.2.9-3.35 8.82-7.24-1.2-1.12-3.8 6.6-3.58-29.4 1.58-7.05 11.52 15.02 22.15L908 417.83l-16.3-16.9-.54 15.98 14.2 16 6.16 28.36 17.77 6.22 23.88 9.72-44.26-7.95-18.6-17.77-16.85-23.88 23.03 11.52-10.65-19.47-2.12-26.5-10.27 2.6-12.4-26.6-34.5 23.95v15.07l-12.44 12.36-15.95-39.83-17.65-21.28-42.57-7.98-12.37-10.6-6.22 3.5 8.87 21.3 14.15-7.08 18.66 14.15-54.92 32.77-28.38-55.8-5.9 1.02 28.94 66.3 28.36-13.3-8.86 31.02-28.34 25.7 4.4 38.04-15.94 12.4-29.2 54.92-29.24 5.32-25.64-62 6.14-28.32-15.9-49.6-54.02 3.55-30.17-29.25 3.72-33.25 162.23-3.12s-161.23-5.92-161.1-7.1l.48-4.33 29.26-35.43 56.66-9.7 3.56 15 23 15.06 8.86-12.4 35.2 9.02 9.13-5.47 3.95-13.28-24.3-4.05-7.86-13.63-5.74-2.9-5.03 17.25-10.74-13.68.7-11.4-14.36-11.5-8.6-4.3.73 11.5 17.95 12.13-9.38 10.82-12.88-17.25-9.27-10.77L567 293.47l-15.04 23.7-22.95-3.6-1.4-28.7 29.33 2.15-9.25-15.04-5.76-12.2 22.22-7.17 12.18-16.47 10.06-.73-2.16-9.33 6.44-8.6 5.02 14.34 28 .73 7.84-20.1 10.82 3.64-3.63-13.62 16.68-5.4-5.18-6.1-15.08 4.27-5.65-13.86-2.2-5.45 6.05-4.33 5.3-13.45-5.68-.9-9.25 11.5-7.92 17.2 8.63 9.3-8.62 22.96-12.86 5-7.23-22.9-15.05 8.57-7.18-4.27.7-22.22 12.95-10.07 10.73-26.53 55.3-28.05 40 25.54C771.65 135 902 101.93 902 101.93l246.94 51.16.86 24.76-23.9 17.74-23.88 5.26.9 20.4-23.05 28.34-.87-31.86 30.08-36.34-18.57 2.7-23.92 19.44-29.25-1.76-6.1 5.88H872.07l150.1 10.08-6.96 5.32 15.92 11.5-3.67 10.15h4.74l2.63 10.7-5.24 18.55 9.66 7.97-12.37 11.5-1.77 23.03-37.2 22.13.9-13.24 20.38-15.1 15.93-33.65.8-27.6-13.36 36.96-15.95 2.64-9.73 14.16 4.4 18.63-11.5 6.13-3.53-16.8-9.78-13.3-13.3 14.2 10.6 30.06-14.52 26.46zm-550.92 55.8l-38.63-23.77-54.33-.7-6.86 13.77-11.23-2.66-7.1-19.7-16.22.28 5.03-19.7-13.55-.17-12.53 12.02-10.6-12.7 6.07-23.57 35.9-8.58 8.3 18.25 9.37-2.14-1.53-17.53 22.5-37.23 30.76-20.34 40.06-22.44-35.4-63.43-12.73 9.26-13.3-28.66-25.53 3.58-2.03 53.1-9.34 2.2-42.73-38.35 6.2-22.26 39.74-11.6-4.48-51.18-100.08 19.62-55.14-26.38-22.25 25-69.2-30.6-34.42 24.73 15.22 19.06-14.83 1.3-2.73 15.13 20.42.93L2.4 176.4l8.7 22.3 17.86 2.52-26.24 25.36 42.53-25.42 23.03-15.63 41.17 20.86 5.15 27.03L221.68 231l-99.33 13.27 22.67 11.9 4.12 47.13 21.9 33.37 35.2 45.75 38.88 10.12 16.85 6.7 20.94 22.57 12.98-3.17-12.4 24.87 16.32 47.36 27.6 20.63-20.06 127.98 13.2 25.97 31.64 5.37-20.55-18.58 21.27-50.68 51.07-51.95 2.15-21.88 26.6-6.42 10-26.1 13.28-5.48-10.24-2.4 9.05-23.56-49.1-18.73-7.34-21.08zm627.06 52.85l-2.9 22.97-19.45-7.07 3.6-12.43-21.28-1.72-27.58 25.7-29.25 10.58 7.16 39.8-2.7 7.9 10.7 6.2 43.36-10.72 16.76 9.77 7.98-4.45 5.32 16.1 22.1 6.2 15.98-9.77 8.85-47.72-23.9-23.9-14.78-27.42zm-107.45-22.15l23.02 4.4 10.65-32.74-6.24-5.35-30.96 19.5 3.53 14.2zm133.7 23.9l-7.94-16.84-36.3-17.67-18.6 1.74 23.9 24.8 13.7.97 5.78-5.4 19.46 12.4zm76.2 87.66l2.66 24.82-27.45 25.7 17.73 1.77 15.9-23.95 12.4-16.8-7.95-.85-13.3-10.7zm-587.9-340.68v25.8l17.9-7.86 5.03-7.93-19.38-37.27-11.4 7.9.7 10.02-10.78 12.97-.75 13.58h12.94l5.73-17.2zm157.97 271.86l-.72 28.7 11.53 5 11.44-34.4-2.9-14.33-19.36 15.03zM418.5 6.88l-59.52-5.64-63.75 53.05 95.23 120 32.62 5.97 2.65-43.4 22.03-10.14 1.27-16.5 22.73-31.8-15.83-24.14L418.5 6.87zm64.9 156.18l7.97-16.3 17.68 8.62-6.25 12.82-11.46 3.95-7.93-9.1z"
						/>
					</svg>

					{/* CLUSTERS */}
					{loaded && this.state.startAnimation && !hideClusters ? (
						<ClustersLayer
							clusters={clusters}
							projection={this.projection}
							hoveredElement={this.state.hoveredElement}
							onClusterClick={this.openCluster.bind(this)}
							onClusterMouseEnter={this.focusElement.bind(this)}
							onClusterMouseLeave={this.unfocusElement.bind(this)}
						/>
					) : null}
					{/* MUSEUMS */}
					{loaded && this.state.startAnimation ? (
						<MuseumsLayer
							museums={museums}
							projection={this.projection}
							hoveredElement={this.state.hoveredElement}
							switchPoint={this.switchPoint}
							onMuseumMouseEnter={this.focusElement.bind(this)}
							onMuseumMouseLeave={this.unfocusElement.bind(this)}
						/>
					) : null}
				</div>
			</div>
		);
	}
}

export default withRouter(Map);
